import { useTranslation } from "react-i18next";
import { storedLocale } from "../../components/Authentication/util";
import { resolveDisplayName } from "./displayName";
import defaultContent from "../../content/common";
import { padAff } from "./affPadding";

// Re-export padAff for backward compatibility
export { padAff } from "./affPadding";

export interface AffAboResult {
  aff: string;
  abo: string;
}

export function separateAffAbo(affAboString: string): AffAboResult {
  const defaultResult: AffAboResult = {
    aff: "",
    abo: "",
  };

  if (!affAboString || typeof affAboString !== "string") {
    return defaultResult;
  }

  const parts = affAboString.split("-");

  if (parts.length !== 2) {
    return defaultResult;
  }

  const [aff, abo] = parts;

  // Validate aff (1-3 digits, will be padded) and abo (digits only)
  if (!/^\d{1,3}$/.test(aff) || !/^\d+$/.test(abo)) {
    return defaultResult;
  }

  return {
    aff: padAff(aff),
    abo,
  };
}

// Custom hook version - for use in React components
// This is used by BronzeIncentives and other components
export const useGetABODisplayName = ({
  aboName,
  aboLocalName,
  isoLanguageCode,
  privacyFlag = false,
}: {
  aboName: string;
  aboLocalName: string;
  isoLanguageCode: string;
  privacyFlag?: boolean;
}) => {
  const { t } = useTranslation(["common"]);
  const locale = storedLocale();

  // Get translated strings
  const confidentialText = t("confidentialUser", defaultContent["confidentialUser"]) as string;
  const notAvailableText = t("notAvailable", defaultContent["notAvailable"]) as string;

  // Use resolveDisplayName with translated strings
  return resolveDisplayName({
    name: aboName,
    localName: aboLocalName,
    privacyFlag,
    languageCode: isoLanguageCode,
    locale,
    confidentialText,
    notAvailableText,
  });
};

// Component version - uses resolveDisplayName with translated strings
export const GetABODisplayName = ({
  aboName,
  aboLocalName,
  isoLanguageCode,
  privacyFlag = false,
}: {
  aboName: string;
  aboLocalName: string;
  isoLanguageCode: string;
  privacyFlag?: boolean;
}) => {
  const { t } = useTranslation(["common"]);
  const locale = storedLocale();

  // Get translated strings
  const confidentialText = t("confidentialUser", defaultContent["confidentialUser"]) as string;
  const notAvailableText = t("notAvailable", defaultContent["notAvailable"]) as string;

  // Use resolveDisplayName with translated strings
  const displayName = resolveDisplayName({
    name: aboName,
    localName: aboLocalName,
    privacyFlag,
    languageCode: isoLanguageCode,
    locale,
    confidentialText,
    notAvailableText,
  });

  return <>{displayName}</>;
};
